import React, { useRef, useCallback } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import DescriptionIcon from "@mui/icons-material/Description";
import InfoIcon from "@mui/icons-material/Info";
import { DocumentUploadCardContentProps } from "./types";

function formatFileSize(bytes: number): string {
  if (bytes < 1024) {
    return `${bytes} B`;
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export const DocumentUploadCardContent: React.FC<DocumentUploadCardContentProps> = ({
  documentTypeName,
  documentTypeDescription,
  status,
  progress,
  uploadedFile,
  errorMessage,
  onFileSelect,
  onRemove,
  onRetry,
  disabled = false,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    if (!disabled && status !== "uploading") {
      fileInputRef.current?.click();
    }
  }, [disabled, status]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        handleClick();
      }
    },
    [handleClick],
  );

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (file) {
        onFileSelect(file);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [onFileSelect],
  );

  const renderContent = () => {
    switch (status) {
      case "idle":
        return (
          <Box
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-label={`Upload ${documentTypeName} document`}
            aria-disabled={disabled}
            onClick={handleClick}
            onKeyDown={handleKeyDown}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              py: 1.5,
              px: 2,
              border: 2,
              borderStyle: "dashed",
              borderColor: disabled ? "action.disabled" : "divider",
              borderRadius: 1,
              backgroundColor: disabled ? "action.disabledBackground" : "action.hover",
              cursor: disabled ? "not-allowed" : "pointer",
              transition: "all 0.2s ease-in-out",
              "&:hover": disabled
                ? {}
                : {
                    borderColor: "primary.main",
                    backgroundColor: "primary.50",
                  },
              "&:focus-visible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: 2,
              },
            }}
          >
            <CloudUploadIcon
              sx={{
                fontSize: 28,
                color: disabled ? "action.disabled" : "primary.main",
              }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="body2" color={disabled ? "text.disabled" : "primary.main"} sx={{ fontWeight: 600 }}>
                Click to upload
              </Typography>
              <Typography variant="caption" color="text.secondary">
                PDF, JPG, PNG up to 10MB
              </Typography>
            </Box>
          </Box>
        );

      case "uploading":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              py: 1.5,
              px: 2,
              border: 2,
              borderStyle: "solid",
              borderColor: "primary.main",
              borderRadius: 1,
              backgroundColor: "primary.50",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1 }}>
              <DescriptionIcon sx={{ color: "primary.main", fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: 500, flex: 1 }} noWrap>
                Uploading...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {progress}%
              </Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 4, borderRadius: 1 }} />
          </Box>
        );

      case "success":
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              py: 1.5,
              px: 2,
              border: 2,
              borderStyle: "solid",
              borderColor: "success.main",
              borderRadius: 1,
              backgroundColor: "success.50",
            }}
          >
            <CheckCircleIcon sx={{ color: "success.main", mr: 1.5, fontSize: 20 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }} noWrap>
                {uploadedFile?.name || "Document uploaded"}
              </Typography>
              {uploadedFile && (
                <Typography variant="caption" color="text.secondary">
                  {formatFileSize(uploadedFile.size)}
                </Typography>
              )}
            </Box>
            <IconButton
              size="small"
              onClick={onRemove}
              disabled={disabled}
              aria-label="Remove uploaded file"
              sx={{ ml: 1 }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        );

      case "error":
        return (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              py: 1.5,
              px: 2,
              border: 2,
              borderStyle: "solid",
              borderColor: "error.main",
              borderRadius: 1,
              backgroundColor: "error.50",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <ErrorIcon sx={{ color: "error.main", mr: 1.5, fontSize: 20 }} />
              <Typography variant="body2" color="error.main" sx={{ fontWeight: 500, flex: 1 }}>
                Upload failed
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 1 }}>
              {errorMessage || "An error occurred. Please try again."}
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<RefreshIcon />}
                onClick={onRetry}
                disabled={disabled}
              >
                Retry
              </Button>
              <Button size="small" variant="text" color="inherit" onClick={onRemove} disabled={disabled}>
                Cancel
              </Button>
            </Box>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Card variant="outlined">
      <CardContent>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {documentTypeName}
          </Typography>
          {documentTypeDescription && (
            <Tooltip title={documentTypeDescription} placement="top" arrow>
              <span
                tabIndex={0}
                style={{ display: "inline-flex", alignItems: "center" }}
                aria-label={`${documentTypeName} information`}
              >
                <InfoIcon sx={{ fontSize: 16, color: "text.secondary" }} />
              </span>
            </Tooltip>
          )}
        </Box>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
        {renderContent()}
      </CardContent>
    </Card>
  );
};

export default DocumentUploadCardContent;
